import React, { useEffect, useState } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import SwimLaneTimeline, { TimelineLane } from '../../common/SwimLaneTimeline';
import api from '../../../services/api';

interface Props {
  personaId: string;
}

const ActivityTimeline: React.FC<Props> = ({ personaId }) => {
  const { darkMode } = useTheme();
  const [lanes, setLanes] = useState<TimelineLane[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const resp = await api.get(`/cross-system/links/persona/${personaId}`);
        const links = resp.data || [];

        // Group events by type into lanes
        const forgeEvents: any[] = [];
        const workflowEvents: any[] = [];
        const artifactEvents: any[] = [];

        for (const link of links) {
          const event = {
            id: link.id,
            start: link.created_at,
            label: `${link.link_type} (${link.source_type})`,
            status: 'success' as const,
            metadata: link.metadata,
          };

          if (link.source_type === 'workflow_phase') {
            workflowEvents.push({
              ...event,
              label: link.metadata?.phase_label || 'Workflow phase',
              status: link.metadata?.phase_status === 'failed' ? 'failure' as const : 'success' as const,
            });
          } else if (link.source_type === 'artifact') {
            artifactEvents.push({
              ...event,
              label: link.metadata?.node_path || 'Artifact',
            });
          } else {
            forgeEvents.push(event);
          }
        }

        const buildLanes: TimelineLane[] = [];
        if (forgeEvents.length > 0) buildLanes.push({ id: 'forge', label: 'Forge', color: '#7c3aed', events: forgeEvents });
        if (workflowEvents.length > 0) buildLanes.push({ id: 'workflow', label: 'Workflows', color: '#2563eb', events: workflowEvents });
        if (artifactEvents.length > 0) buildLanes.push({ id: 'artifacts', label: 'Artifacts', color: '#06b6d4', events: artifactEvents });

        setLanes(buildLanes);
      } catch (err) {
        console.error('Failed to load activity:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [personaId]);

  if (loading) return <div className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Loading timeline...</div>;

  if (lanes.length === 0) {
    return (
      <div className={`p-8 text-center rounded-lg border ${darkMode ? 'border-gray-700 text-gray-500' : 'border-gray-200 text-gray-400'}`}>
        No activity recorded yet. Activity appears here after this persona is used in workflows or generates artifacts.
      </div>
    );
  }

  return (
    <div className={`rounded-xl border p-4 ${darkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`}>
      <div style={{ height: Math.max(lanes.length * 56 + 60, 200) }}>
        <SwimLaneTimeline lanes={lanes} />
      </div>
    </div>
  );
};

export default ActivityTimeline;
